import type { BlockStatus, CaptureSession, ValidationIssue } from '@/shared/capture/capture-session';
import {
  BLOCK_STATUSES,
  CAPTURE_SESSION_STATUSES,
  READINESS_STATES,
  summarizeReadiness,
  VALIDATION_SEVERITIES,
} from '@/shared/capture/capture-session';

export const INVENTORIZATION_BLOCK_IDS = {
  context: 'context',
  identity: 'identity',
  position: 'position',
  readings: 'readings',
  review: 'review',
  technical: 'technical',
} as const;

export const inventorizationBlocks = [
  {
    id: INVENTORIZATION_BLOCK_IDS.context,
    label: 'Context',
    description: 'Scan or create, inherit location, capture context photo.',
  },
  {
    id: INVENTORIZATION_BLOCK_IDS.identity,
    label: 'Identity',
    description: 'Confirm serial, QR/API key, record type, manufacturer, and model.',
  },
  {
    id: INVENTORIZATION_BLOCK_IDS.readings,
    label: 'Registers',
    description: 'Capture register photos and confirmed values.',
  },
  {
    id: INVENTORIZATION_BLOCK_IDS.position,
    label: 'Position',
    description: 'Confirm GPS, building/floor, and floorplan placement or waiver.',
  },
  {
    id: INVENTORIZATION_BLOCK_IDS.technical,
    label: 'Remote reading',
    description: 'Fill missing meter-type details, outputs, mount, and remote-reading data.',
  },
  {
    id: INVENTORIZATION_BLOCK_IDS.review,
    label: 'Review',
    description: 'Check whether the technician can leave the meter.',
  },
] as const;

export type InventorizationBlockId = (typeof INVENTORIZATION_BLOCK_IDS)[keyof typeof INVENTORIZATION_BLOCK_IDS];

export function createInventorizationBlockStatuses(): Record<InventorizationBlockId, BlockStatus> {
  return {
    [INVENTORIZATION_BLOCK_IDS.context]: BLOCK_STATUSES.inProgress,
    [INVENTORIZATION_BLOCK_IDS.identity]: BLOCK_STATUSES.empty,
    [INVENTORIZATION_BLOCK_IDS.readings]: BLOCK_STATUSES.empty,
    [INVENTORIZATION_BLOCK_IDS.position]: BLOCK_STATUSES.empty,
    [INVENTORIZATION_BLOCK_IDS.technical]: BLOCK_STATUSES.empty,
    [INVENTORIZATION_BLOCK_IDS.review]: BLOCK_STATUSES.blocked,
  };
}

export function createInventorizationIssue(
  blockId: InventorizationBlockId,
  code: string,
  message: string,
): ValidationIssue {
  return {
    blockId,
    code,
    message,
    severity: VALIDATION_SEVERITIES.blocking,
  };
}

export function readInventorizationBlockStatus(issues: ValidationIssue[], isReady = true) {
  return issues.length === 0 && isReady ? BLOCK_STATUSES.valid : BLOCK_STATUSES.inProgress;
}

export function updateInventorizationBlock(
  session: CaptureSession,
  blockId: InventorizationBlockId,
  status: BlockStatus,
  updatedAt: string,
): CaptureSession {
  const blockStatuses = {
    ...createInventorizationBlockStatuses(),
    ...session.blockStatuses,
    [blockId]: status,
  };
  const readiness = summarizeReadiness(blockStatuses);

  return {
    ...session,
    status:
      readiness.state === READINESS_STATES.ready ? CAPTURE_SESSION_STATUSES.ready : CAPTURE_SESSION_STATUSES.draft,
    blockStatuses,
    updatedAt,
  };
}
